"use client";

import { formatTimeWithPreferences } from "@workspace/shared-utils";
import type {
	DbSetReview,
	FestivalDetails,
	ProcessedSet,
	Review,
} from "@workspace/types";
import { memo, Suspense, useCallback } from "react";
import { TimeEmptyState } from "@/components/features/festival/states";
import { RealtimeSetCard } from "@/components/shared/realtime-set-card";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import type { TabVariant } from "./use-tab-variant";

interface TimeTabProps {
	// Core data
	groupSetsByTimeAndDay: Record<string, Record<string, ProcessedSet[]>>;
	unfilteredGroupSetsByTimeAndDay?: Record<
		string,
		Record<string, ProcessedSet[]>
	>;
	festivalDetails: FestivalDetails | null;
	festivalSlug?: string;

	// Variant configuration
	variant: TabVariant;

	// Authentication/ownership
	authState: boolean; // isOwnProfile for personal, isAuthenticated for public

	// Reviews data - supports both formats
	setReviews?: DbSetReview[]; // Personal view format
	reviews?: Review[]; // Public view simplified format

	// Schedule data
	savedSchedule: string[];

	// Display state
	selectedDay: string;
	sortAscending: boolean;

	// Personal view specific
	currentShowOnlySaved?: boolean;
	isSetReviewedOrSaved?: (setId: string) => boolean;
	onClearFilters?: () => void;

	// Event handlers
	onStartReview: (setId: string) => void;
	onToggleSetInSchedule: (setId: string) => void;
	onAuthRequired?: () => void;

	// Read-only mode
	readOnly?: boolean;
}

export const TimeTab = memo(function TimeTab({
	groupSetsByTimeAndDay,
	unfilteredGroupSetsByTimeAndDay,
	festivalDetails,
	festivalSlug,
	variant,
	authState,
	reviews,
	savedSchedule,
	selectedDay,
	sortAscending,
	currentShowOnlySaved = false,
	isSetReviewedOrSaved,
	onClearFilters,
	onStartReview,
	onToggleSetInSchedule,
	onAuthRequired,
	readOnly,
}: TimeTabProps) {
	// Get user preferences for time formatting
	const userPreferences = useUserPreferences();

	// Always use RealtimeSetCard for real-time updates
	const SetCardComponent = RealtimeSetCard;

	// Helper function to format time labels based on user preferences
	const formatTimeLabel = useCallback(
		(timeLabel: string): string => {
			// Extract hour from timeLabel (e.g., "9:00PM" -> 21)
			let hour: number;
			if (timeLabel === "12:00AM") {
				hour = 0;
			} else if (timeLabel === "12:00PM") {
				hour = 12;
			} else {
				const hourPart = timeLabel.split(":")[0];
				const baseHour = Number.parseInt(hourPart || "0", 10);
				hour = timeLabel.includes("AM") ? baseHour : baseHour + 12;
			}

			// Create a date object for the given hour and format it
			const date = new Date();
			date.setHours(hour, 0, 0, 0);
			return formatTimeWithPreferences(date, userPreferences);
		},
		[userPreferences],
	);

	const isReadOnly = readOnly ?? !authState;

	// Helper functions for time sorting
	const getHour = (ts: string) => {
		if (ts === "12:00AM") return 0;
		if (ts === "12:00PM") return 12;
		const hourPart = ts.split(":")[0];
		return Number.parseInt(hourPart || "0") + (ts.includes("AM") ? 0 : 12);
	};

	const getFestHour = (ts: string) => {
		let h = getHour(ts);
		// Use schedule_day_start_hour from festival details (defaults to 6 if not specified)
		const dayStartHour = festivalDetails?.schedule_day_start_hour ?? 6;
		const scheduleStart = festivalDetails?.schedule_start_hour ?? 0;
		const scheduleEnd = festivalDetails?.schedule_end_hour ?? 24;
		const crossesMidnight = scheduleStart > scheduleEnd;
		if (festivalDetails?.schedule_overnight && crossesMidnight && h >= 0 && h < dayStartHour) h += 24;
		return h;
	};

	const dayGroup = groupSetsByTimeAndDay[selectedDay];
	const unfilteredDayGroup = unfilteredGroupSetsByTimeAndDay?.[selectedDay];

	// Check if empty state is due to filters
	const isEmptyDueToFilters = !!(
		unfilteredDayGroup &&
		Object.keys(unfilteredDayGroup).length > 0 &&
		(!dayGroup || Object.keys(dayGroup).length === 0)
	);

	if (!dayGroup || Object.keys(dayGroup).length === 0) {
		return (
			<TimeEmptyState
				selectedDay={selectedDay}
				isFiltered={
					isEmptyDueToFilters ||
					(variant === "personal" && currentShowOnlySaved)
				}
				onClearFilters={
					(isEmptyDueToFilters ||
						(variant === "personal" && currentShowOnlySaved)) &&
					onClearFilters
						? onClearFilters
						: undefined
				}
			/>
		);
	}

	const sortedTimePeriods = Object.keys(dayGroup).sort((a, b) => {
		return sortAscending
			? getFestHour(a) - getFestHour(b)
			: getFestHour(b) - getFestHour(a);
	});

	return (
		<div className="grid gap-4">
			{sortedTimePeriods.map((timeLabel) => {
				const setsForTime = dayGroup[timeLabel] || [];
				const shouldFilterSets =
					variant === "personal" &&
					currentShowOnlySaved &&
					isSetReviewedOrSaved;
				const filteredSets = shouldFilterSets
					? setsForTime.filter((set) => isSetReviewedOrSaved(set.id))
					: setsForTime;

				// Check if this time slot has sets in unfiltered data
				const unfilteredSetsForTime = unfilteredDayGroup?.[timeLabel] || [];
				const isTimeEmptyDueToFilters =
					unfilteredSetsForTime.length > 0 && filteredSets.length === 0;

				return (
					<div
						key={timeLabel}
						id={`time-section-${timeLabel.replace(/[\s:.]/g, "-")}`}
						className="space-y-2.5 min-w-0"
					>
						<h3 className="font-semibold">{formatTimeLabel(timeLabel)}</h3>
						{filteredSets.length === 0 ? (
							<TimeEmptyState
								timeLabel={timeLabel}
								selectedDay={selectedDay}
								isFiltered={
									isTimeEmptyDueToFilters ||
									(variant === "personal" && currentShowOnlySaved)
								}
								onClearFilters={
									onClearFilters &&
									(isTimeEmptyDueToFilters ||
										(variant === "personal" && currentShowOnlySaved))
										? onClearFilters
										: undefined
								}
							/>
						) : (
							<div className="space-y-3">
								{filteredSets.map((set) => {
									// Find the review for this set from batched parent data
									// null = parent checked, no review exists (keeps per-set hook disabled)
									// undefined = parent didn't batch (falls back to per-set hook)
									const review = reviews
										? (reviews.find((r) => r.setId === set.id) ?? null)
										: undefined;

									return (
										<Suspense
											key={set.id}
											fallback={
												<div className="h-20 animate-pulse rounded-lg bg-muted" />
											}
										>
											<SetCardComponent
												set={set}
												review={review}
												savedSchedule={savedSchedule}
												festivalSlug={festivalSlug}
												variant={variant}
												onStartReview={onStartReview}
												onToggleSetInSchedule={onToggleSetInSchedule}
												readOnly={isReadOnly}
												onAuthRequired={onAuthRequired}
												layout="horizontal"
												hideDayLabel
											/>
										</Suspense>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
});
